/**
 * IK chain definitions for the mannequin skeleton.
 *
 * Each chain is an ordered list of bone names from root to end effector.
 * When the user grabs a bone, we find the chain where that bone is the
 * end effector and solve IK along that chain.
 */

export interface IKChainDef {
  /** Bone names from chain root to end effector */
  bones: string[];
  /** Pole target direction in object-local space — defines which way mid-joints bend.
   *  Transformed to world space by the mannequin's rotation before solving. */
  poleDirection: [number, number, number];
  /** Solver type: "analytical" for 2-bone limbs, "jacobian" (DLS) for
   *  multi-bone chains and all other cases. */
  solverType: "analytical" | "jacobian";
  /**
   * Per-joint mobility weights (0 = locked, 1 = fully free).
   * Controls how much each joint participates in the IK solve.
   * Higher weight → joint moves more freely; lower weight → joint resists motion.
   *
   * Used by the Jacobian DLS solver to scale each joint's contribution to the
   * solve. Lower weight → joint contributes less rotation → solver prefers to
   * rotate higher-weight joints first (e.g., neck before pelvis).
   *
   * Must match the length of `bones`. Defaults to all 1s if omitted.
   */
  jointWeights?: number[];
}

// Chains are defined with the END EFFECTOR LAST.
// The solver fixes the chain root and moves the end effector toward the target.
// Pole directions define the preferred bend plane (object-local space).
const CHAINS: IKChainDef[] = [
  // Arms: elbows point forward (+Y). Shoulder is handled by secondary motion, not IK.
  {
    bones: ["upper_armL", "forearmL", "handL"],
    poleDirection: [0, 1, 0],
    solverType: "analytical",
  },
  {
    bones: ["upper_armR", "forearmR", "handR"],
    poleDirection: [0, 1, 0],
    solverType: "analytical",
  },
  // Legs: knees point backward (-Y)
  {
    bones: ["thighL", "shinL", "footL"],
    poleDirection: [0, -1, 0],
    solverType: "analytical",
  },
  {
    bones: ["thighR", "shinR", "footR"],
    poleDirection: [0, -1, 0],
    solverType: "analytical",
  },
  // Spine: bends backward (-Y)
  // Joint weights encode anatomical mobility: pelvis is relatively fixed,
  // lumbar spine moderately mobile, and the neck/head are the most free.
  // This makes the solver prefer rotating the neck over the pelvis when the head is dragged.
  {
    bones: ["hips", "spine", "spine1", "neck", "head"],
    poleDirection: [0, -1, 0],
    solverType: "jacobian",
    jointWeights: [0.25, 0.45, 0.55, 0.85, 0.95],
  },
];

/**
 * Find the IK chain for a bone grab. Uses the full chain from root
 * to the selected bone.
 *
 * For analytical (limb) chains, the sub-chain must have exactly 3 bones
 * (2 segments) to use the analytical solver. Shorter sub-chains fall back
 * to the chain's declared solver type (jacobian or fabrik).
 */
export function findChainForBone(boneName: string): {
  chain: string[];
  effectorIndex: number;
  poleDirection: [number, number, number];
  solverType: "analytical" | "jacobian";
  jointWeights?: number[];
} | null {
  for (const def of CHAINS) {
    const idx = def.bones.indexOf(boneName);
    if (idx >= 0) {
      const subChain = def.bones.slice(0, idx + 1);
      if (subChain.length < 2) {
        // Single-bone grab: extend to include the next bone so the solver
        // has at least one segment. Always use Jacobian DLS.
        const end = Math.min(def.bones.length, idx + 2);
        const subWeights = def.jointWeights?.slice(idx, end);
        return {
          chain: def.bones.slice(idx, end),
          effectorIndex: end - idx - 1,
          poleDirection: def.poleDirection,
          solverType: "jacobian",
          jointWeights: subWeights,
        };
      }
      // Use analytical solver only for full 3-bone (2-segment) limb chains.
      // All other cases use Jacobian DLS.
      const effectiveSolver =
        def.solverType === "analytical" && subChain.length === 3
          ? "analytical"
          : "jacobian";
      const subWeights = def.jointWeights?.slice(0, idx + 1);
      return {
        chain: subChain,
        effectorIndex: subChain.length - 1,
        poleDirection: def.poleDirection,
        solverType: effectiveSolver,
        jointWeights: subWeights,
      };
    }
  }
  return null;
}
